import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import {
  reportSectionNavItems,
  type ReportSectionId,
} from '../../helpers/analysis-report';

export function ReportSectionNav({
  onStickyChange,
  stickyAction,
}: {
  onStickyChange: (isStuck: boolean) => void;
  stickyAction?: ReactNode;
}) {
  const navRef = useRef<HTMLElement | null>(null);
  const scrollRootRef = useRef<HTMLElement | null>(null);
  const programmaticScrollTimeoutRef = useRef<number | null>(null);
  const scrollRequestIdRef = useRef(0);
  const [activeSection, setActiveSection] = useState<ReportSectionId>(reportSectionNavItems[0].id);

  const getNavHeight = useCallback(() => navRef.current?.getBoundingClientRect().height ?? 0, []);
  const getSectionMarkerY = useCallback(() => {
    const scrollRoot = scrollRootRef.current;
    const scrollRootTop = scrollRoot?.getBoundingClientRect().top ?? 0;

    return scrollRootTop + getNavHeight();
  }, [getNavHeight]);
  const getSectionPositions = useCallback(
    () =>
      reportSectionNavItems
        .map((item) => {
          const section = document.getElementById(item.id);

          if (!section) {
            return null;
          }

          return { id: item.id, top: section.getBoundingClientRect().top };
        })
        .filter((section): section is { id: ReportSectionId; top: number } => Boolean(section)),
    [],
  );

  const scrollToSection = useCallback(
    (sectionId: ReportSectionId, behavior: ScrollBehavior = 'smooth') => {
      const scrollRoot = scrollRootRef.current ?? navRef.current?.closest<HTMLElement>('main');
      const section = document.getElementById(sectionId);

      if (!scrollRoot || !section) {
        return;
      }

      scrollRequestIdRef.current += 1;
      const scrollRequestId = scrollRequestIdRef.current;

      if (programmaticScrollTimeoutRef.current) {
        window.clearTimeout(programmaticScrollTimeoutRef.current);
        programmaticScrollTimeoutRef.current = null;
      }

      const targetTop =
        scrollRoot.scrollTop + section.getBoundingClientRect().top - getSectionMarkerY();
      scrollRoot.scrollTo({ top: targetTop, behavior });

      programmaticScrollTimeoutRef.current = window.setTimeout(
        () => {
          if (scrollRequestId !== scrollRequestIdRef.current) {
            return;
          }

          programmaticScrollTimeoutRef.current = null;
        },
        behavior === 'smooth' ? 500 : 0,
      );
    },
    [getSectionMarkerY],
  );

  useEffect(() => {
    const scrollRoot = navRef.current?.closest<HTMLElement>('main') ?? null;
    scrollRootRef.current = scrollRoot;
    const observedSections = reportSectionNavItems
      .map((item) => document.getElementById(item.id))
      .filter((section): section is HTMLElement => Boolean(section));

    if (!scrollRoot) {
      return;
    }

    const syncActiveSection = () => {
      const scrollRootTop = scrollRoot.getBoundingClientRect().top;
      const navTop = navRef.current?.getBoundingClientRect().top ?? scrollRootTop;
      const markerY = getSectionMarkerY() + 1;
      const sectionPositions = getSectionPositions();

      onStickyChange(navTop <= scrollRootTop + 1);

      let currentSection = sectionPositions[0];
      const isScrollable = scrollRoot.scrollHeight > scrollRoot.clientHeight + 2;
      const isScrolledToPageEnd =
        isScrollable &&
        scrollRoot.scrollTop > 0 &&
        scrollRoot.scrollTop + scrollRoot.clientHeight >= scrollRoot.scrollHeight - 2;

      for (const section of sectionPositions) {
        if (section.top <= markerY) {
          currentSection = section;
        }
      }

      if (isScrolledToPageEnd) {
        currentSection = sectionPositions[sectionPositions.length - 1];
      }

      if (currentSection) {
        setActiveSection((previousSection) =>
          previousSection === currentSection.id ? previousSection : currentSection.id,
        );
      }
    };

    const observer = new IntersectionObserver(() => syncActiveSection(), {
      root: scrollRoot,
      rootMargin: '-140px 0px -55% 0px',
      threshold: [0, 0.01, 0.1, 0.25],
    });

    for (const section of observedSections) {
      observer.observe(section);
    }

    syncActiveSection();
    const handleScroll = () => {
      syncActiveSection();
    };

    scrollRoot.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', syncActiveSection);

    return () => {
      observer.disconnect();
      if (programmaticScrollTimeoutRef.current) {
        window.clearTimeout(programmaticScrollTimeoutRef.current);
        programmaticScrollTimeoutRef.current = null;
      }
      scrollRequestIdRef.current += 1;
      scrollRoot.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', syncActiveSection);
    };
  }, [getSectionMarkerY, getSectionPositions, onStickyChange]);

  useEffect(() => {
    const activeTab = navRef.current?.querySelector<HTMLElement>(
      `[data-report-section-tab="${activeSection}"]`,
    );
    const tabsList = activeTab?.closest<HTMLElement>('[data-report-section-tabs-list]');

    if (!activeTab || !tabsList) {
      return;
    }

    if (tabsList.scrollWidth <= tabsList.clientWidth) {
      return;
    }

    const activeTabRect = activeTab.getBoundingClientRect();
    const tabsListRect = tabsList.getBoundingClientRect();
    const centeredLeftOffset =
      activeTabRect.left - tabsListRect.left - (tabsListRect.width - activeTabRect.width) / 2;

    tabsList.scrollTo({
      left: tabsList.scrollLeft + centeredLeftOffset,
      behavior: 'smooth',
    });
  }, [activeSection]);

  const handleSelectSection = (sectionId: string) => {
    const nextSectionId = sectionId as ReportSectionId;
    setActiveSection(nextSectionId);
    scrollToSection(nextSectionId);
    window.history.replaceState(null, '', `#${sectionId}`);
  };

  return (
    <nav
      ref={navRef}
      data-campaign-analysis-screen-nav
      aria-label="Report sections"
      className="border-border bg-muted sticky top-0 z-10 border-y px-8 py-1 shadow-sm backdrop-blur"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Tabs
          value={activeSection}
          onValueChange={handleSelectSection}
          className="min-w-0 flex-1 gap-0"
        >
          <TabsList
            data-report-section-tabs-list
            className="scrollbar-hide flex h-auto w-full min-w-0 justify-start gap-1 overflow-x-auto"
          >
            {reportSectionNavItems.map((item) => (
              <TabsTrigger
                key={item.id}
                value={item.id}
                data-report-section-tab={item.id}
                className="h-auto min-h-8 flex-none px-2 py-1.5 text-center leading-4 sm:px-3 lg:px-4"
              >
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {stickyAction ? (
          <div data-campaign-analysis-report-actions className="flex shrink-0 items-center gap-1">
            {stickyAction}
          </div>
        ) : null}
      </div>
    </nav>
  );
}
